import { z } from "zod"

import { CompanyRegistrationType } from "@/graphql/codegen/graphql"
import { isFieldFilled, isLengthValid } from "@/utils/forms"

const federalRegex = /^\d{2}-\d{7}$|^\d{9}$/

export const RegistrationSchema = z
  .object({
    companyId: z.string(),
    companyRegistrationId: z.string().optional(),
    registrationType: z
      .enum([CompanyRegistrationType.Local, CompanyRegistrationType.State, CompanyRegistrationType.Federal])
      .nullable(),
    locationId: z.string().nullable().optional(),
    registrationNumber: z.string(),
    registrationDocument: z.optional(z.instanceof(File)).nullable(),
    taxExemptionNumber: z
      .string()
      .trim()
      .optional()
      .refine(
        (value) => {
          return !isFieldFilled(value) || isLengthValid(value, 3, 128)
        },
        {
          message: "Tax exemption number must be empty or between 3 and 128 characters long",
        }
      ),
    taxExemptionDocument: z.optional(z.instanceof(File)).nullable(),
  })
  .refine(
    ({ registrationType, registrationNumber }) => {
      if (registrationType === CompanyRegistrationType.Federal) {
        return federalRegex.test(registrationNumber)
      } else {
        return isLengthValid(registrationNumber, 3, 128)
      }
    },
    ({ registrationType }) => ({
      path: ["registrationNumber"],
      message:
        registrationType === CompanyRegistrationType.Federal
          ? "Federal registration number must be 9 digits or in the format xx-xxxxxxx"
          : "Registration number must be between 3 and 128 characters long",
    })
  )
  .refine(
    ({ registrationType, locationId }) => {
      if (registrationType === CompanyRegistrationType.State || registrationType === CompanyRegistrationType.Local) {
        return isFieldFilled(locationId)
      }
      return true
    },
    {
      path: ["locationId"],
      message: "Location is mandatory",
    }
  )

export type RegistrationFormValues = z.infer<typeof RegistrationSchema>
export type RegistrationType = z.infer<typeof RegistrationSchema>["registrationType"]
